"use client";

import { useEffect, useState, useMemo } from "react";
import { useSignalR } from "../../providers/SignalRProvider";
import OrderTableSkeleton from "./components/OrderTableSkeleton";
import OrdersTable from "./components/OrdersTable";
import ConnectionStatus from "./components/ConnectionStatus";
import { getDomainFromLocalStorage } from "../../utils/commonfunction";
import normalizeKeys from "../../utils/commonfunction";
import { X } from "lucide-react";

export const getStatColor = (status) => {
  const colors = {
    Pending: "bg-yellow-100",
    Accept: "bg-blue-100",
    Dispatch: "bg-purple-100",
    Delivered: "bg-green-100",
    Cancel: "bg-red-100",
  };
  return colors[status] || "bg-white";
};

/* ================= FLOATING FILTER ================= */
function FloatingStatusFilter({
  orderStatuses,
  statusFilter,
  setStatusFilter,
  statusCounts,
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <button
        onClick={() => setStatusFilter("All")}
        className={`h-10 px-2 sm:px-3 xl:px-4 rounded-lg border text-sm font-bold transition-colors
          ${statusFilter === "All" ? "bg-black text-white border-black" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
      >
        All ({statusCounts.total})
      </button>

      {Object.entries(orderStatuses).map(([id, name]) => (
        <button
          key={id}
          onClick={() => setStatusFilter(name)}
          className={`h-10 px-2 sm:px-3 xl:px-4 rounded-lg border text-sm font-bold transition-colors
            ${statusFilter === name ? "bg-black text-white border-black" : `bg-white text-slate-700 border-slate-300 hover:border-slate-500 ${getStatColor(name)}`}`}
        >
          {name} ({statusCounts[name] || 0})
        </button>
      ))}
    </div>
  );
}

export default function NewOrdersManagmentPage() {
  const { connection, status, onReconnect } = useSignalR();

  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const [completeData, setCompleteData] = useState({});

  const handleRiderChange = (orderNumber, newRider) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.OrderNumber === orderNumber ? { ...o, Rider: newRider } : o,
      ),
    );
  };

  const handleBranchTransfer = (orderNumber, branchId) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.OrderNumber === orderNumber ? { ...o, BranchId: branchId } : o,
      ),
    );
  };

  const handleDeliveryTimeUpdate = (orderNumber, adjustmentMinutes) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.OrderNumber === orderNumber
          ? { ...o, DeliveryTime: o.DeliveryTime + adjustmentMinutes }
          : o,
      ),
    );
  };

  const fetchAllOrders = (conn) => {
    const userId = Number(localStorage.getItem("USER_ID"));
    const domain = getDomainFromLocalStorage();
    if (!domain || !userId || !conn || conn.state !== "Connected") return;

    setOrdersLoading(true);
    conn
      .invoke("OrderHistoryRequest", domain, userId, "AllOrders")
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    if (!connection || connection.state !== "Connected") return;

    connection.on("OrderStatusUpdate", (response) => {
      if (response.dataPayload?.Success) {
        const newStatus = response.dataPayload.OrderStatusName;
        const logs = response.dataPayload.OrderStatusLogs;

        setOrders((prev) =>
          prev.map((o) =>
            o.OrderToken === response.orderToken
              ? { ...o, Status: newStatus, OrderStatusLogs: logs }
              : o,
          ),
        );
      }
    });

    connection.on("NewOrder", (neworder) => {
      const normalized = normalizeKeys(neworder);
      setOrders((prev) => [normalized, ...prev]);

      const orderNumber = normalized.OrderNumber;
      setNewOrderIds((prev) => new Set(prev).add(orderNumber));

      setTimeout(() => {
        setNewOrderIds((prev) => {
          const updated = new Set(prev);
          updated.delete(orderNumber);
          return updated;
        });
      }, 5000);
    });

    connection.on("AllOrders", (data) => {
      if (data.dataPayload?.Orders) {
        setOrders(data.dataPayload.Orders.map(normalizeKeys));
        console.log(data.dataPayload)
      }
      setOrderStatuses(data.dataPayload.OrderStatuses || []);
      setCompleteData(data.dataPayload);
      setOrdersLoading(false);
    });

    fetchAllOrders(connection);

    const unsubscribe = onReconnect(() => {
      fetchAllOrders(connection);
    });

    return () => {
      connection.off("NewOrder");
      connection.off("AllOrders");
      unsubscribe();
    };
  }, [connection]);

  const statusCounts = useMemo(() => {
    const counts = { total: orders?.length || 0 };
    Object.values(orderStatuses).forEach((name) => (counts[name] = 0));

    orders.forEach((o) => {
      const s = o.Status || "Pending";
      if (counts.hasOwnProperty(s)) counts[s]++;
    });

    return counts;
  }, [orders, orderStatuses]);

  const filteredOrders = useMemo(() => {
    let filtered = [...(orders || [])];

    if (statusFilter !== "All") {
      filtered = filtered.filter(
        (o) => (o.Status || "Pending") === statusFilter,
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          (o.OrderToken || "").toLowerCase().includes(q) ||
          (o.CustomerDetails?.FullName || "").toLowerCase().includes(q) ||
          (o.CustomerDetails?.MobileNumber || "").toLowerCase().includes(q),
      );
    }

    return filtered;
  }, [orders, searchQuery, statusFilter]);

  console.log({orders});
  const isLoading = status !== "connected" || ordersLoading;

  return (
    <div className="p-3 space-y-4">
      <div className="w-full flex items-center justify-between">
        <div className="flex gap-3 items-center flex-1 min-w-0">
          {/* Title + status — inline, no extra row */}
          {/* <div className="flex items-center gap-2 shrink-0">
            <ConnectionStatus />
            <h1 className="text-xl font-bold whitespace-nowrap">Live Orders</h1>
          </div> */}

          {/* Search grows but leaves breathing room before filter */}
          <div className="flex gap-1 items-center flex-1 min-w-0 mr-4">
            <div className="relative w-full max-w-sm">
              <input
                id="SearchOrderInput"
                type="text"
                placeholder="Search order ID, Customer Name, Phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-10 w-full rounded-md border px-3 pr-8 text-sm outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("All");
                }}
                className={`absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-opacity
        ${searchQuery || statusFilter !== "All" ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Filter stays right, never squished */}
        <div className="shrink-0">
          {/* SEARCH (moved up) */}
          <FloatingStatusFilter
            orderStatuses={orderStatuses}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            statusCounts={statusCounts}
          />
        </div>
      </div>

      {/* RESULTS INFO */}
      {/* {(searchQuery || statusFilter !== "All") && (
        <div className="text-xs text-slate-500">
          Showing <b>{filteredOrders.length}</b> of <b>{orders.length}</b>{" "}
          orders
        </div>
      )} */}

      {/* TABLE */}
      <div className="rounded-lg border bg-white">
        {isLoading ? (
          <OrderTableSkeleton />
        ) : (
          <OrdersTable
            orders={filteredOrders}
            allOrders={orders}
            OrderStatuses={orderStatuses}
            onRiderChange={handleRiderChange}
            onBranchTransfer={handleBranchTransfer}
            onDeliveryTimeUpdate={handleDeliveryTimeUpdate}
            newOrderIds={newOrderIds}
            riders={completeData?.Riders}
            branches={completeData?.Branches}
          />
        )}
      </div>
    </div>
  );
}
